import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Badge,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import "./Signup.css";
import { encrypt } from "../../Encrypdecryp/Crypt.js";

const Signup = () => {
  const [fullName, setFullName] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (password !== confirmPassword) {
      const errMsg = "Passwords do not match.";
      setError(errMsg);
      toast.error(errMsg, {
        position: "top-center",
        autoClose: 3000,
        style: {
          background: "#f8d7da",
          color: "#721c24",
          borderRadius: "12px",
          fontSize: "14px",
          fontWeight: "600",
        },
      });
      setIsLoading(false);
      return;
    }

    const encryptedEmail = encrypt(email.trim());
    const encryptedPassword = encrypt(password.trim());
    const encryptedFullName = encrypt(fullName.trim());
    const encryptedTokenId = encrypt(tokenId.trim());

    if (!encryptedEmail || !encryptedPassword || !encryptedFullName || !encryptedTokenId) {
      const errMsg = "Encryption failed. Unable to encrypt credentials.";
      setError(errMsg);
      toast.error(errMsg, {
        position: "top-center",
        autoClose: 3000,
        style: {
          background: "#f8d7da",
          color: "#721c24",
          borderRadius: "12px",
          fontSize: "14px",
          fontWeight: "600",
        },
      });
      setIsLoading(false);
      return;
    }

    const payload = {
      email: encryptedEmail,
      password: encryptedPassword,
      full_name: encryptedFullName,
      tid_token: encryptedTokenId,
    };

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      if (!API_BASE_URL) throw new Error("API_BASE_URL is not defined.");

      const response = await axios.post(`${API_BASE_URL}/users/signup`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <strong style={{ fontSize: '16px' }}>✅ Account Created!</strong>
            <span style={{ fontSize: '14px' }}>Signup successful. Please login to continue.</span>
          </div>,
          {
            position: "top-center",
            autoClose: 2500,
            hideProgressBar: false,
            style: {
              background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
              color: '#155724',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(40, 167, 69, 0.3)',
              border: '2px solid #28a745',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '350px',
            },
            progressStyle: {
              background: '#28a745',
            },
          }
        );
        
        setTimeout(() => navigate("/login"), 1500);
      }
    } catch (err) {
      console.error("Signup error:", err);
      let errorMessage = "Signup failed. Please try again.";

      if (err.response) {
        const { status, data } = err.response;
        if (status === 409) errorMessage = "User already exists.";
        else if (status === 402) errorMessage = data?.detail || "Token issue. Contact support.";
        else if (status === 400) errorMessage = data?.detail || "Invalid input.";
      }

      setError(errorMessage);
      
      toast.error(
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <strong style={{ fontSize: '16px' }}>❌ Signup Failed</strong>
          <span style={{ fontSize: '14px' }}>{errorMessage}</span>
        </div>,
        {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          style: {
            background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
            color: '#721c24',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(220, 53, 69, 0.3)',
            border: '2px solid #dc3545',
            fontSize: '14px',
            fontWeight: '600',
            minWidth: '350px',
          },
          progressStyle: {
            background: '#dc3545',
          },
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="signup-container" maxWidth={false}>
      <Box className="signup-grid">
        {/* Left Side - Branding */}
        <Box className="signup-brand-section">
          <Box className="brand-content">
            <Typography variant="h2" className="brand-title">
              Join boTeye TradeDocs
            </Typography>
            <Typography variant="body1" className="brand-subtitle">
              Streamline Trade Finance with AI-Powered Intelligence
            </Typography>
            <Box className="brand-features">
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">Automated document extraction & processing</Typography>
              </Box>
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">Advanced RAG-powered document search</Typography>
              </Box>
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">Secure transaction chain management</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Side - Signup Form */}
        <Box className="signup-form-section">
          <Paper className="signup-paper" elevation={0}>
            <Box className="form-header">
              <Typography variant="h4" className="form-title">
                Create Your Account
              </Typography>
              <Typography variant="body2" className="form-subtitle">
                Fill in your details to get started
              </Typography>
            </Box>

            {error && (
              <Box className="error-message">
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            <form onSubmit={handleSignup} autoComplete="off">
              <Box className="input-group">
                <TextField
                  label="Full Name"
                  variant="outlined"
                  fullWidth
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="input-field"
                  required
                  autoComplete="off"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person className="input-icon" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box className="input-group">
                <TextField
                  label="Token ID"
                  variant="outlined"
                  fullWidth
                  value={tokenId}
                  onChange={(e) => setTokenId(e.target.value)}
                  className="input-field"
                  required
                  autoComplete="off"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Badge className="input-icon" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box className="input-group">
                <TextField
                  label="Email Address"
                  variant="outlined"
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  required
                  autoComplete="new-email"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email className="input-icon" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box className="input-group">
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  required
                  autoComplete="new-password"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock className="input-icon" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box className="input-group">
                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  required
                  autoComplete="new-password"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock className="input-icon" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                className="signup-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            <Divider className="divider">
              <Typography variant="body2" className="divider-text">
                OR
              </Typography>
            </Divider>

            <Typography className="footer-text">
              Already have an account?{" "}
              <Link to="/login" className="login-link">
                Sign In
              </Link>
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default Signup;